/**
 * Detached Panel Page
 * Renders a single panel (viewer or editor) in a separate browser window.
 * Communicates with the parent ParsingReviewWorkstation via BroadcastChannel.
 *
 * Route: /parsing-review/:reviewItemId/detached/:panelType
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import DocumentViewer from './DocumentViewer/DocumentViewer';
import SmartInput from './CorrectionPanel/SmartInput/SmartInput';
import FieldEditor from './CorrectionPanel/FieldEditor/FieldEditor';
import TypeSelector from './CorrectionPanel/TypeSelector';
import ActionBar from './ActionBar/ActionBar';
import { useBroadcastSync, MSG } from './hooks/useBroadcastSync';
import { validateData } from './utils/fieldSchemas';

const DetachedPanelPage = () => {
  const { reviewItemId, panelType } = useParams();
  const smartInputRef = useRef(null);
  const hasReceivedState = useRef(false);

  // Local state — driven by STATE_SYNC from parent
  const [currentItem, setCurrentItem] = useState(null);
  const [correction, setCorrection] = useState({
    documentType: 'flight',
    parsedData: {},
    originalData: {},
    smartInputValue: '',
    isDirty: false,
    validationErrors: [],
    notes: ''
  });
  const [documentZoom, setDocumentZoom] = useState(100);
  const [documentViewMode, setDocumentViewMode] = useState('html');
  const [canApprove, setCanApprove] = useState(false);
  const [isAssignedToMe, setIsAssignedToMe] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [connected, setConnected] = useState(true);

  // Handle messages from parent
  const handleMessage = useCallback((type, payload) => {
    switch (type) {
      case MSG.STATE_SYNC:
        hasReceivedState.current = true;
        setCurrentItem(payload.currentItem);
        setCorrection(payload.correction);
        setDocumentZoom(payload.documentZoom);
        setDocumentViewMode(payload.documentViewMode);
        setCanApprove(payload.canApprove);
        setIsAssignedToMe(payload.isAssignedToMe);
        setIsSaving(false);
        break;

      case MSG.REATTACH:
        // Parent wants us to close
        window.close();
        break;

      default:
        break;
    }
  }, []);

  const { sendMessage } = useBroadcastSync(handleMessage);

  // Send CHILD_READY on mount to request initial state
  useEffect(() => {
    sendMessage(MSG.CHILD_READY, { panelType, reviewItemId });

    // Set window title
    const title = panelType === 'viewer'
      ? 'Parsing Review - Document Viewer'
      : 'Parsing Review - Editor';
    document.title = title;

    // Notify parent on close
    const handleBeforeUnload = () => {
      sendMessage(MSG.CHILD_CLOSING);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendMessage, panelType, reviewItemId]);

  // Detect if parent is gone (no STATE_SYNC received within 3 seconds)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!hasReceivedState.current) {
        setConnected(false);
      }
    }, 3000);
    return () => clearTimeout(timeout);
  }, []);

  // --- Correction handlers (relay to parent) ---
  const updateField = useCallback((field, value) => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'updateField', field, value });
    // Optimistic local update
    setCorrection(prev => ({
      ...prev,
      parsedData: { ...prev.parsedData, [field]: value },
      isDirty: true
    }));
  }, [sendMessage]);

  const updateLeg = useCallback((legIndex, legData) => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'updateLeg', legIndex, legData });
    setCorrection(prev => {
      const legs = [...(prev.parsedData.legs || [])];
      legs[legIndex] = { ...legs[legIndex], ...legData };
      return { ...prev, parsedData: { ...prev.parsedData, legs }, isDirty: true };
    });
  }, [sendMessage]);

  const addLeg = useCallback(() => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'addLeg' });
    setCorrection(prev => {
      const legs = [...(prev.parsedData.legs || [])];
      legs.push({ leg_number: legs.length + 1 });
      return { ...prev, parsedData: { ...prev.parsedData, legs }, isDirty: true };
    });
  }, [sendMessage]);

  const removeLeg = useCallback((legIndex) => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'removeLeg', legIndex });
    setCorrection(prev => {
      const legs = [...(prev.parsedData.legs || [])];
      legs.splice(legIndex, 1);
      legs.forEach((leg, idx) => { leg.leg_number = idx + 1; });
      return { ...prev, parsedData: { ...prev.parsedData, legs }, isDirty: true };
    });
  }, [sendMessage]);

  const handleTypeChange = useCallback((newType) => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'changeDocumentType', documentType: newType });
    setCorrection(prev => ({
      ...prev,
      documentType: newType,
      parsedData: { ...prev.parsedData, document_type: newType },
      isDirty: true
    }));
  }, [sendMessage]);

  const handleSmartInputApply = useCallback((parsedData) => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'applyParsedData', parsedData });
    setCorrection(prev => ({
      ...prev,
      parsedData: { ...prev.parsedData, ...parsedData },
      isDirty: true
    }));
  }, [sendMessage]);

  const setSmartInputValue = useCallback((smartInputValue) => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'setSmartInputValue', smartInputValue });
    setCorrection(prev => ({ ...prev, smartInputValue }));
  }, [sendMessage]);

  const setNotes = useCallback((notes) => {
    sendMessage(MSG.CORRECTION_UPDATE, { action: 'setNotes', notes });
    setCorrection(prev => ({ ...prev, notes }));
  }, [sendMessage]);

  // --- Action handlers (relay to parent) ---
  const handleAction = useCallback((action) => {
    setIsSaving(true);
    sendMessage(MSG.ACTION_EVENT, { action });
  }, [sendMessage]);

  const handleReattach = useCallback(() => {
    sendMessage(MSG.REATTACH_REQUEST);
    window.close();
  }, [sendMessage]);

  // --- Priority/Status badge helpers ---
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // --- Disconnected state ---
  if (!connected && !hasReceivedState.current) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center p-8">
          <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.757 6.757M9.878 9.878l-3.12-3.12m7.364 7.364l3.12 3.12M1 1l22 22" />
          </svg>
          <h2 className="mt-4 text-lg font-medium text-gray-900">Connection lost</h2>
          <p className="mt-1 text-gray-500">The parent window is no longer available.</p>
          <button
            onClick={() => window.close()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
          >
            Close window
          </button>
        </div>
      </div>
    );
  }

  // --- Loading state ---
  if (!currentItem) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <svg className="w-8 h-8 mx-auto animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="mt-2 text-gray-500">Waiting for data from main window...</p>
        </div>
      </div>
    );
  }

  // --- Viewer Panel ---
  if (panelType === 'viewer') {
    return (
      <div className="flex flex-col h-screen bg-gray-100">
        {/* Header bar */}
        <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h1 className="text-sm font-semibold text-gray-900">Document Viewer</h1>
              <span className="text-xs text-gray-500 truncate max-w-xs">
                {currentItem.email_subject}
              </span>
            </div>
            <button
              onClick={handleReattach}
              className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md flex items-center gap-1"
              title="Reattach to main window"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              Reattach
            </button>
          </div>
        </header>

        {/* Document viewer full-size */}
        <div className="flex-1 bg-white overflow-hidden">
          <DocumentViewer
            html={currentItem.original_email_html}
            subject={currentItem.email_subject}
            sender={currentItem.email_from}
            receivedAt={currentItem.email_received_at}
            attachments={currentItem.attachments || []}
            zoom={documentZoom}
            onZoomChange={setDocumentZoom}
            viewMode={documentViewMode}
            onViewModeChange={setDocumentViewMode}
            className="h-full"
          />
        </div>
      </div>
    );
  }

  // --- Editor Panel ---
  if (panelType === 'editor') {
    return (
      <div className="flex flex-col h-screen bg-gray-100">
        {/* Header bar */}
        <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <h1 className="text-sm font-semibold text-gray-900">Correction Editor</h1>
              <span className="text-xs text-gray-500 truncate max-w-xs">
                {currentItem.email_subject}
              </span>
            </div>
            <button
              onClick={handleReattach}
              className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md flex items-center gap-1"
              title="Reattach to main window"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              Reattach
            </button>
          </div>
        </header>

        {/* Editor content */}
        <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
          {/* Panel header */}
          <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between">
              <TypeSelector
                value={correction.documentType}
                onChange={handleTypeChange}
                disabled={!canApprove && !isAssignedToMe}
              />

              {currentItem && (
                <div className="flex items-center gap-2 text-sm">
                  <span className={`px-2 py-0.5 text-xs rounded border ${getPriorityColor(currentItem.priority)}`}>
                    {currentItem.priority}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(currentItem.status)}`}>
                    {currentItem.status?.replace('_', ' ')}
                  </span>
                </div>
              )}
            </div>

            {/* Item info */}
            {currentItem && (
              <div className="mt-2 text-xs text-gray-500">
                <div className="truncate">{currentItem.organization_name}</div>
                <div className="truncate">{currentItem.email_subject}</div>
              </div>
            )}

            {/* Issues summary */}
            {currentItem && (() => {
              const liveValidation = validateData(correction.documentType, correction.parsedData);
              if (liveValidation.isValid) return null;
              return (
                <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                  <div className="text-xs font-medium text-red-800">
                    Issues detected:
                  </div>
                  <ul className="mt-1 text-xs text-red-600 space-y-0.5">
                    {liveValidation.errors.slice(0, 4).map((err, idx) => (
                      <li key={`err-${idx}`}>• {err}</li>
                    ))}
                    {liveValidation.errors.length > 4 && (
                      <li className="text-red-500">+ {liveValidation.errors.length - 4} more</li>
                    )}
                  </ul>
                </div>
              );
            })()}
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Smart Input */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Smart Input
              </h3>
              <SmartInput
                ref={smartInputRef}
                documentType={correction.documentType}
                value={correction.smartInputValue}
                onChange={setSmartInputValue}
                onApply={handleSmartInputApply}
                disabled={!canApprove && !isAssignedToMe}
              />
            </div>

            {/* Field Editor */}
            <FieldEditor
              documentType={correction.documentType}
              data={correction.parsedData}
              confidence={currentItem?.ai_confidence_score ? {} : {}}
              onChange={updateField}
              onLegChange={updateLeg}
              onAddLeg={addLeg}
              onRemoveLeg={removeLeg}
              disabled={!canApprove && !isAssignedToMe}
              showValidation={true}
            />
          </div>
        </div>

        {/* Action Bar */}
        <ActionBar
          currentIndex={0}
          totalItems={0}
          item={currentItem}
          isAssigned={!!currentItem?.assigned_to_user_id}
          isAssignedToMe={isAssignedToMe}
          canApprove={canApprove}
          isSaving={isSaving}
          isDirty={correction.isDirty}
          onAssign={() => handleAction('assign')}
          onApprove={() => handleAction('approve')}
          onApproveAndNext={() => handleAction('approveAndNext')}
          onReject={() => handleAction('reject')}
          onSkip={() => handleAction('skip')}
          onDelete={() => handleAction('delete')}
          onReset={() => handleAction('reset')}
          notes={correction.notes}
          onNotesChange={setNotes}
        />
      </div>
    );
  }

  // Unknown panel type
  return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <p className="text-gray-500">Unknown panel type: {panelType}</p>
    </div>
  );
};

export default DetachedPanelPage;
